import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { shape, arrayOf, string, func, number } from 'prop-types';
import { createPortal } from 'react-dom';
import cx from 'classnames';

import { ChevronDownIcon, XMarkIcon } from '@snapask/common-components';

import { isMobile } from '@snapask/common-components/dist/utils/detectMobileType';

import styles from './Dropdown.scss';

function Dropdown({ value, options, onChange, className }) {
  const [openDropdown, setOpenDropdown] = useState(false);
  const selectorRef = useRef(null);
  const listRef = useRef(null);

  const getOptionValue = option => option.value;

  const selectedOption = useMemo(
    () =>
      options.find(option => getOptionValue(option) === value) || options[0],
    [options, value]
  );

  useEffect(() => {
    document.addEventListener('mousedown', onClickDropdownOutside);
    return () =>
      document.removeEventListener('mousedown', onClickDropdownOutside);
  }, [onClickDropdownOutside]);

  const toggleDropdown = () => setOpenDropdown(state => !state);

  const onClickDropdownOutside = useCallback(event => {
    if (
      !listRef.current ||
      listRef.current.contains(event.target) ||
      selectorRef.current.contains(event.target)
    )
      return;

    setOpenDropdown(false);
  }, []);

  const onClickDropdownItem = optionValue => () => {
    if (optionValue === value) return;

    onChange(optionValue);
    setOpenDropdown(false);
  };

  const renderDropdownList = isOpen => (
    <div
      className={cx(styles.dropdown, {
        [styles.open]: isOpen,
      })}
    >
      <div className={styles.header}>
        <XMarkIcon width={24} height={24} onClick={toggleDropdown} />
      </div>
      <ul ref={listRef} className={styles.list}>
        {options.map(option => (
          <li
            onClick={onClickDropdownItem(option.value)}
            key={option.value}
            className={cx(styles.listItem, {
              [styles.selected]: getOptionValue(option) === value,
            })}
          >
            {option.label}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div
      className={cx(styles.root, { [styles.open]: openDropdown }, className)}
    >
      <div
        ref={selectorRef}
        className={styles.selector}
        onClick={toggleDropdown}
      >
        {selectedOption.label}
        <ChevronDownIcon
          width={24}
          height={24}
          className={styles.selectorArrowIcon}
        />
      </div>
      {isMobile()
        ? createPortal(
            <div className={cx(styles.modal, { [styles.open]: openDropdown })}>
              <div className={styles.mask} />
              {renderDropdownList(openDropdown)}
            </div>,
            document.getElementById('modal')
          )
        : renderDropdownList(openDropdown)}
    </div>
  );
}

Dropdown.propTypes = {
  value: number.isRequired,
  onChange: func.isRequired,
  className: string,
  options: arrayOf(
    shape({
      value: number.isRequired,
      label: string.isRequired,
    })
  ),
};

export default Dropdown;



// WEBPACK FOOTER //
// ./app/containers/Premium/components/SubscriptionSection/components/Dropdown/Dropdown.jsx